import { FC, useCallback, useState } from 'react';
import {
  Card,
  ListContainer,
  ListDataRequestParams,
  ListData,
  useListDataRequest,
} from '../../../ui';
import { usePatientContext } from '../../containers/PatientContainer/usePatientContext';
import {
  getPatientQuestionnaireData,
  getQuestionnaires,
  assignQuestionnaire,
  PatientQuestionnaireDataModel,
} from '../../network/questionnaires';
import { PatientQuestionnairesDataGrid } from '../PatientQuestionnairesDataGrid';
import { AssignQuestionnaireModal } from '../AssignQuestionnaireModal';
import { Button, Tooltip } from '@mui/material';
import { enqueueSnackbar } from 'notistack';

export const PatientQuestionnairesWidget: FC = () => {
  const { patient, reload: reloadPatient } = usePatientContext();
  const { data: questionnaires } = useListDataRequest(getQuestionnaires);
  const [openModal, setOpenModal] = useState(false);
  const [isAssigningQuestionnaire, setIsAssigningQuestionnaire] =
    useState<boolean>(false);
  const [componentKey, setComponentKey] = useState<number>(0);
  const questionnaireData = patient.questionnaire_data;

  // Create a wrapper function that matches the ListDataRequest signature
  const fetchQuestionnaires = useCallback(
    async (
      params?: ListDataRequestParams
    ): Promise<ListData<PatientQuestionnaireDataModel>> => {
      const combinedParams = {
        ...params,
        patient_id: patient.id,
      };
      return await getPatientQuestionnaireData(combinedParams);
    },
    [patient.id]
  );

  const scheduleQuestionnaireButton = () => {
    return (
      <Tooltip
        title={
          questionnaireData.current_questionnaire_status === 'incomplete'
            ? 'Patient has an incomplete questionnaire'
            : questionnaireData.current_questionnaire_status === 'scheduled'
            ? 'Patient has one questionnaire currently scheduled'
            : ''
        }
        placement='left'
      >
        <span>
          <Button
            variant='contained'
            color='primary'
            disabled={
              questionnaireData.current_questionnaire_status === 'scheduled' ||
              questionnaireData.current_questionnaire_status === 'incomplete'
            }
            onClick={() => setOpenModal(true)}
          >
            Assign Questionnaire
          </Button>
        </span>
      </Tooltip>
    );
  };

  const handleAssignQuestionnaire = async (
    questionnaireId: number,
    dueDate: string
  ) => {
    try {
      setIsAssigningQuestionnaire(true);

      // Make API call
      await assignQuestionnaire(questionnaireId, patient.id, dueDate);

      // Close modal
      setOpenModal(false);

      // Show success notification
      enqueueSnackbar('Questionnaire assigned successfully', {
        variant: 'success',
      });

      // Reload the patient
      reloadPatient();

      // Simply increment the key to refresh the component
      setComponentKey((prevKey) => prevKey + 1);
    } catch (error) {
      console.error('Error assigning questionnaire:', error);
      enqueueSnackbar('Failed to assign questionnaire', {
        variant: 'error',
      });
    } finally {
      setIsAssigningQuestionnaire(false);
    }
  };

  return questionnaireData.organization_uses_questionnaires ? (
    <Card title='Questionnaires' rightActions={scheduleQuestionnaireButton()}>
      <AssignQuestionnaireModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        questionnaires={questionnaires || []}
        onAssignQuestionnaire={handleAssignQuestionnaire}
        isAssigningQuestionnaire={isAssigningQuestionnaire}
      />

      <ListContainer key={componentKey} dataRequest={fetchQuestionnaires}>
        <PatientQuestionnairesDataGrid height='50vh' />
      </ListContainer>
    </Card>
  ) : null;
};
